import { motion } from "framer-motion";
import { Check, Zap, Crown, ArrowRight } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const tiers = [
  {
    id: "luxury",
    badge: "Beta Launch Offer",
    name: "Luxury Property Tier",
    bestFor: "₦50,000,000+ properties, smart homes, premium estates",
    price: "₦300,000",
    originalPrice: "₦400,000",
    icon: Zap,
    features: [
      "15–20 8K frames",
      "48–72 hour delivery",
      "Agent tracking links",
      "Social media kit",
      "Geotag verification badge",
      "WhatsApp booking integration",
    ],
    highlighted: false,
    cta: "Get Luxury Tour",
    whatsapp: "Hello Vista360! I'm interested in the Luxury Property Tier (₦300,000 beta offer). Can we schedule a capture?",
  },
  {
    id: "founding",
    badge: "Limited — 10 Spots",
    name: "Founding Partner Program",
    bestFor: "First 10 property owners & developers",
    price: "₦250,000",
    originalPrice: null,
    icon: Crown,
    features: [
      "All Luxury tier features",
      "12-month featured homepage placement",
      "Priority support",
      "Vista360 TV feature episode",
      "Early access to new features",
      "Exclusive Founding Partner badge",
    ],
    highlighted: true,
    cta: "Claim Founding Spot",
    whatsapp: "Hello Vista360! I want to join the Founding Partner Program (₦250,000). Are there still spots available?",
  },
];

export const BetaPricing = () => {
  return (
    <section className="section-padding bg-muted">
      <div className="container-tight">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-primary font-semibold text-sm uppercase tracking-wider"
          >
            Packages
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-3"
          >
            360° Virtual Tour Packages
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-muted-foreground"
          >
            Beta Launch Offer — Limited Time
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className={`relative bg-card rounded-2xl shadow-premium border overflow-hidden ${
                tier.highlighted
                  ? "border-secondary ring-2 ring-secondary/20"
                  : "border-border"
              }`}
            >
              {tier.highlighted && (
                <div className="absolute top-0 inset-x-0 h-1 gold-gradient" />
              )}

              <div className="p-6 lg:p-8">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold mb-4 ${
                    tier.highlighted
                      ? "badge-360"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {tier.badge}
                </span>

                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl hero-gradient flex items-center justify-center">
                    <tier.icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground">{tier.name}</h3>
                </div>

                <p className="text-muted-foreground text-sm mb-4">
                  Best for: {tier.bestFor}
                </p>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-display text-4xl font-bold text-foreground">{tier.price}</span>
                  {tier.originalPrice && (
                    <span className="text-muted-foreground line-through text-lg">{tier.originalPrice}</span>
                  )}
                  <span className="text-muted-foreground text-sm">per property</span>
                </div>

                <WhatsAppButton
                  message={tier.whatsapp}
                  label={tier.cta}
                  className={`w-full justify-center mb-6 ${
                    tier.highlighted ? "" : ""
                  }`}
                />

                <div className="space-y-3">
                  {tier.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <Check
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          tier.highlighted ? "text-secondary" : "text-primary"
                        }`}
                      />
                      <span className="text-foreground text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <WhatsAppButton
            message="Hello Vista360! I'd like a custom quote for my property. Can we discuss?"
            label="Contact for Custom Quote"
            className="gold-gradient text-foreground shadow-gold"
          />
        </motion.div>
      </div>
    </section>
  );
};
